<script setup>
import { computed, ref } from 'vue';
import BaseBubble from 'next/message/bubbles/Base.vue';
import FormattedContent from './FormattedContent.vue';
import ExpandableContent from './ExpandableContent.vue';
import AttachmentChips from 'next/message/chips/AttachmentChips.vue';
import TranslationToggle from 'dashboard/components-next/message/TranslationToggle.vue';
import { MESSAGE_TYPES } from '../../constants';
import { useMessageContext } from '../../provider.js';
import { useTranslations } from 'dashboard/composables/useTranslations';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import {
  DuplicateContactException,
  ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';

const {
  content,
  attachments,
  additionalAttributes,
  contentAttributes,
  messageType,
} = useMessageContext();

const { hasTranslations, translationContent } =
  useTranslations(contentAttributes);
const store = useStore();
const { t } = useI18n();

const renderOriginal = ref(false);

const renderContent = computed(() => {
  if (renderOriginal.value) {
    return content.value;
  }

  if (hasTranslations.value) {
    return translationContent.value;
  }

  return content.value;
});

const isTemplate = computed(() => {
  return messageType.value === MESSAGE_TYPES.TEMPLATE;
});

const valueForKey = (source, key) => {
  if (!source) return undefined;

  return source[key] || source[`${key[0].toLowerCase()}${key.slice(1)}`];
};

const componentType = component =>
  valueForKey(component, 'type')?.toString().toUpperCase();

const renderedButtonUrl = button => {
  const url = valueForKey(button, 'url');
  if (!url) return '';

  const parameter = valueForKey(button, 'parameter');
  return parameter ? url.replace(/{{\s*[^}]+\s*}}/g, parameter) : url;
};

const templateDisplayFromComponents = computed(() => {
  const templateParams =
    additionalAttributes.value?.templateParams ||
    additionalAttributes.value?.template_params ||
    {};
  const components = valueForKey(templateParams, 'components') || [];
  const footerComponent = components.find(
    component => componentType(component) === 'FOOTER'
  );
  const buttonsComponent = components.find(
    component => componentType(component) === 'BUTTONS'
  );
  const buttons = (valueForKey(buttonsComponent, 'buttons') || [])
    .map(button => ({
      text: valueForKey(button, 'text'),
      type: valueForKey(button, 'type'),
      url: renderedButtonUrl(button),
    }))
    .filter(button => button.text);

  return {
    footer: valueForKey(footerComponent, 'text'),
    buttons,
  };
});

const templateDisplay = computed(() => {
  return (
    contentAttributes.value?.templateDisplay ||
    contentAttributes.value?.template_display ||
    templateDisplayFromComponents.value ||
    {}
  );
});

const normalizedContactText = computed(() => {
  return String(renderContent.value || '')
    .replace(/<br\s*\/?\s*>/gi, '\n')
    .replace(/<\/(p|div|li)>/gi, '\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\r/g, '\n')
    .replace(/\u00a0/g, ' ')
    .replace(/\*/g, '')
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
    .join('\n');
});

const lineValueForLabels = labels => {
  const labelPattern = labels.join('|');
  const match = normalizedContactText.value.match(
    new RegExp(
      String.raw`(?:^|\n)\s*(?:${labelPattern})(?:\s*\(\d+\))?\s*:\s*(.+)`,
      'i'
    )
  );

  return match?.[1]?.trim();
};

const textContactInfo = computed(() => {
  if (!/(^|\n)\s*(contact|contato)\s*:?/i.test(normalizedContactText.value)) {
    return null;
  }

  const name = lineValueForLabels(['name', 'nome']);
  const phoneNumber = lineValueForLabels([
    'number',
    'número',
    'numero',
    'telefone',
    'phone',
  ]);
  const rawPhoneNumber = phoneNumber?.replace(/\D/g, '');

  if (!rawPhoneNumber) return null;

  return {
    name,
    phoneNumber,
    rawPhoneNumber,
  };
});

function openContactNewTab(contactId) {
  const accountId = window.location.pathname.split('/')[3];
  const url = `/app/accounts/${accountId}/contacts/${contactId}`;
  window.open(url, '_blank');
}

async function filterContactByNumber(searchCandidate) {
  const queryPayload = {
    payload: [
      {
        attribute_key: 'phone_number',
        filter_operator: 'equal_to',
        values: [`+${searchCandidate}`, searchCandidate],
        attribute_model: 'standard',
        custom_attribute_type: '',
      },
    ],
  };

  const contacts = await store.dispatch('contacts/filter', {
    queryPayload,
    resetState: false,
  });
  return contacts.shift();
}

async function addTextContact() {
  const contactInfo = textContactInfo.value;
  if (!contactInfo) return;

  try {
    let contact = await filterContactByNumber(contactInfo.rawPhoneNumber);
    if (!contact) {
      contact = await store.dispatch('contacts/create', {
        name: contactInfo.name || contactInfo.phoneNumber,
        phone_number: `+${contactInfo.rawPhoneNumber}`,
      });
      useAlert(t('CONTACT_FORM.SUCCESS_MESSAGE'));
    }
    openContactNewTab(contact.id);
  } catch (error) {
    if (error instanceof DuplicateContactException) {
      if (error.data.includes('phone_number')) {
        useAlert(t('CONTACT_FORM.FORM.PHONE_NUMBER.DUPLICATE'));
      }
    } else if (error instanceof ExceptionWithMessage) {
      useAlert(error.data);
    } else {
      useAlert(t('CONTACT_FORM.ERROR_MESSAGE'));
    }
  }
}

const templateFooter = computed(() => templateDisplay.value?.footer || '');

// Media header of the template (image, video or document) stored when the message was sent.
const templateHeaderMedia = computed(() => {
  const header = templateDisplay.value?.header;
  const url = header?.mediaUrl || header?.media_url || '';
  if (!url.startsWith('https://')) return null;

  return {
    format: String(header.format || '').toUpperCase(),
    url,
    name: header.mediaName || header.media_name || '',
  };
});

const templateButtons = computed(() =>
  Array.isArray(templateDisplay.value?.buttons)
    ? templateDisplay.value.buttons
    : []
);

const isEmpty = computed(() => {
  return (
    !content.value && !attachments.value?.length && !templateHeaderMedia.value
  );
});

const handleSeeOriginal = () => {
  renderOriginal.value = !renderOriginal.value;
};
</script>

<template>
  <BaseBubble class="px-4 py-3" data-bubble-name="text">
    <div class="gap-3 flex flex-col">
      <a
        v-if="templateHeaderMedia?.format === 'IMAGE'"
        :href="templateHeaderMedia.url"
        target="_blank"
        rel="noopener noreferrer"
        class="skip-context-menu -mx-1 -mt-1 block overflow-hidden rounded-lg"
      >
        <img
          :src="templateHeaderMedia.url"
          :alt="
            templateHeaderMedia.name || $t('CONVERSATION.TEMPLATE_HEADER_IMAGE')
          "
          class="block max-h-[280px] max-w-[280px] rounded-lg object-contain"
          loading="lazy"
        />
      </a>
      <video
        v-else-if="templateHeaderMedia?.format === 'VIDEO'"
        :src="templateHeaderMedia.url"
        controls
        preload="metadata"
        class="skip-context-menu -mx-1 -mt-1 max-h-[280px] max-w-[280px] rounded-lg"
      />
      <a
        v-else-if="templateHeaderMedia?.format === 'DOCUMENT'"
        :href="templateHeaderMedia.url"
        target="_blank"
        rel="noopener noreferrer"
        class="skip-context-menu flex min-h-9 items-center gap-2 rounded-md bg-black/10 px-3 py-2 text-sm ring-1 ring-black/10"
      >
        <span class="i-hugeicons-file-01 size-4 shrink-0" aria-hidden="true" />
        <span class="min-w-0 truncate">
          {{
            templateHeaderMedia.name ||
            $t('CONVERSATION.TEMPLATE_HEADER_DOCUMENT')
          }}
        </span>
      </a>
      <span v-if="isEmpty" class="text-n-slate-11">
        {{ $t('CONVERSATION.NO_CONTENT') }}
      </span>
      <ExpandableContent v-if="renderContent">
        <FormattedContent :content="renderContent" />
      </ExpandableContent>
      <div
        v-if="templateFooter"
        class="template-footer relative -mx-4 mt-0.5 px-4 pt-3 text-xs opacity-80"
      >
        {{ templateFooter }}
      </div>
      <button
        v-if="textContactInfo"
        type="button"
        class="skip-context-menu flex min-h-9 items-center justify-center rounded-md bg-white/90 px-3 py-2 text-sm font-medium text-n-blue-11 shadow-sm ring-1 ring-black/10 hover:bg-white"
        @click="addTextContact"
      >
        {{ t('CONVERSATION.SAVE_CONTACT') }}
      </button>
      <div v-if="templateButtons.length" class="flex flex-col gap-1">
        <a
          v-for="(button, index) in templateButtons"
          :key="`${button.text}-${index}`"
          class="skip-context-menu flex min-h-9 items-center justify-center rounded-md bg-white/90 px-3 py-2 text-sm font-medium text-n-blue-11 shadow-sm ring-1 ring-black/10 hover:bg-white"
          :href="button.url || undefined"
          :target="button.url ? '_blank' : undefined"
          rel="noopener noreferrer"
        >
          {{ button.text }}
        </a>
      </div>
      <TranslationToggle
        v-if="hasTranslations"
        class="-mt-3"
        :showing-original="renderOriginal"
        @toggle="handleSeeOriginal"
      />
      <AttachmentChips :attachments="attachments" class="gap-2" />
      <template v-if="isTemplate">
        <div
          v-if="contentAttributes.submittedEmail"
          class="px-3 py-2 rounded-lg bg-black/10 ring-1 ring-black/10 !text-[var(--message-bubble-text)] break-all"
        >
          {{ contentAttributes.submittedEmail }}
        </div>
      </template>
    </div>
  </BaseBubble>
</template>

<style scoped>
.template-footer::before {
  position: absolute;
  top: 0;
  right: 1rem;
  left: 1rem;
  height: 1px;
  content: '';
  background: linear-gradient(
    90deg,
    transparent,
    currentcolor 20%,
    currentcolor 80%,
    transparent
  );
  opacity: 0.2;
}
</style>

<style>
p:last-child {
  margin-bottom: 0;
}
</style>
